import React, { Component } from 'react';
import { styled, StyledProps } from '@glitz/react';
import { Plus24x24 } from 'Shared/Icons/Plus';
import * as style from 'Shared/Style';
import Button, { Appearance as ButtonAppearance } from 'Shared/Button';

const spacingStyle = {
  // When the accordion is stretched to full viewport width we want
  // to narrows the content like with page components
  marginLeft: 'auto',
  marginRight: 'auto',
};

type HeaderWrapperType = React.HTMLAttributes<HTMLDivElement> & {
  
};
const HeaderWrapper: React.StatelessComponent<HeaderWrapperType> = props => {
  const { ...restProps } = props;
  return (
    <styled.Div
      {...restProps}
    />
  );
};

const Header = styled(Button, {
  width: '100%',
  height: '100%',
  textAlign: 'left',
  paddingTop: 0,
  paddingBottom: 0
});

type HeaderLayoutPropType = React.HTMLAttributes<HTMLDivElement> &
  StyledProps & {
    padding: boolean;
  };

const HeaderLayout = styled((props: HeaderLayoutPropType) => {
  const { padding, compose, ...restProps } = props;
  return (
    <styled.Div
      {...restProps}
      css={compose({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        ...spacingStyle,
      })}
    />
  );
});

const DURATION = 300;

type BodyStyleType = React.HTMLAttributes<HTMLDivElement> &
  StyledProps & {
    shouldTranslateHeight: boolean;
  };
const Body = styled((props: BodyStyleType) => {
  const { shouldTranslateHeight, compose, ...restProps } = props;
  return (
    <styled.Div
      {...restProps}
      css={compose({
        position: 'relative',
        overflow: 'hidden',
        ...spacingStyle,
        ...(shouldTranslateHeight && {
          ...style.transition({
            property: 'height',
            duration: `${DURATION}ms`,
            willChange: true,
          }),
        }),
      })}
    />
  );
});

const Panel = styled.div({
  position: 'absolute',
  right: 0,
  bottom: 0,
  left: 0,
});

const IconWrap = styled.div({
  display: 'inline-flex',
})

const Plus = styled(Plus24x24, {
  transition: {
    property: 'transform',
    duration: 300,
  },
});

export const AccordionHeader = styled.span({
  lineHeight: '130%',
  fontWeight: 500,
  fontSize: 20,
  marginBottom: 0,
  display: 'inline-block'
});

type StateType = {
  height?: number;
  shouldTranslateHeight?: boolean;
};

type PropType = {
  header: string | React.ReactNode;
  onToggle: () => void;
  isOpen?: boolean;
  className?: string;
  hasLinks?: boolean;
  extraPadding?: boolean;
  noBorder?: boolean;
  children?: string | React.ReactNode;
  upperCaseHeading?: boolean;
  headerFontSize?: string;
  iconWidth?: string;
  isCompact?: boolean;
  borderWidth?: number; 
  fontWeight?: number;
  disabled?: boolean;
 /*  css?: StyledDecorator | StyleOrStyleArray<Style>; */
};

const Accordion = styled(
  class AccordionClass extends Component<PropType & StyledProps, StateType> {
    mounted = false;
    state = {
      height: 0,
      shouldTranslateHeight: true,
    };
    componentWillReceiveProps(nextProps: PropType) {
      if (nextProps.isOpen !== this.props.isOpen) {
        this.setState({ shouldTranslateHeight: true });
      } else {
        this.setState({ shouldTranslateHeight: false });
      }
    }
    shouldComponentUpdate(nextProps: PropType, nextState: StateType) {
      return (
        this.props.isOpen !== nextProps.isOpen ||
        this.props.header !== nextProps.header ||
        this.props.onToggle !== nextProps.onToggle ||
        this.props.children !== nextProps.children ||
        this.state.height !== nextState.height
      );
    }
    componentDidMount() {
      this.mounted = true;
    }
    componentWillUnmount() {
      this.mounted = false;
    }
    updateOffsetHeight(ref: HTMLElement) {
      if (ref) {
        // ref to prevent browser freeze
        requestAnimationFrame(() => {
          const height = ref.offsetHeight;
          if (this.mounted && Math.abs(this.state.height - height) > 2) {
            this.setState({ height });
          }
        });
      }
    }
    render() {
      const { shouldTranslateHeight, height } = this.state;
      const { borderWidth = 1 } = this.props;
      return (
        <styled.Div 
          css={this.props.compose({
            ...(!this.props.noBorder && {
              borderBottom: {
                style: 'solid',
                width: borderWidth + 'px',
                color: style.colors.monochrome.black,
              },
              marginBottom: '16px',
              paddingBottom: '16px',
            }),
          /* ...this.props.compose(), */
          })}
          >
          <HeaderWrapper>
            <Header
              onClick={() => !this.props.disabled && this.props.onToggle()}
              appearance={[ButtonAppearance.Full, ButtonAppearance.Bare]}
              id={this.props.className}
              className={this.props.isOpen && this.props.className === "size-guide-button-js" ? "open" : ""}       
            >
              <HeaderLayout padding={this.props.extraPadding} css={{...(this.props.disabled && { cursor: 'default'})}}>
                {typeof this.props.header === 'string' ? (
                  <AccordionHeader css={{...(this.props.disabled && { color: style.colors.monochrome.mediumGrey}), ...(this.props.upperCaseHeading && { textTransform: 'uppercase'}), ...(this.props.fontWeight && { fontWeight: this.props.fontWeight}), ...(this.props.headerFontSize && { fontSize: this.props.headerFontSize, lineHeight: '100%'})}}>{this.props.header}</AccordionHeader>
                ) : (
                  this.props.header
                )}
                {!this.props.disabled && 
                 <IconWrap>
                  <Plus css={{
                      transform: this.props.isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                      width: this.props.isCompact ? '16px' : '24px',
                      ...(this.props.iconWidth && {
                        width: this.props.iconWidth
                      })
                    }} />
                  </IconWrap> }
              </HeaderLayout>
            </Header>
          </HeaderWrapper>
          <Body
            shouldTranslateHeight={shouldTranslateHeight}
            css={{ height: this.props.isOpen && height ? `${height}px` : '0px' }}
          >
            <Panel ref={ref => this.updateOffsetHeight(ref)}>{this.props.children}</Panel>
          </Body>
        </styled.Div>
      );
    }
  },
);

export default Accordion;

export const ToggleableField = styled(Accordion, {

});
